import { useCallback, useEffect, useRef } from "react";

import type { GeneratedSoundItem } from "@ui/components/sound-effects/SoundEffectsGenerationWidget";

import { generateTtsfx } from "@/app/_api/generateTtsfx";

import cover1 from "../images/audio-cover-1.png";
import cover2 from "../images/audio-cover-2.png";
import cover3 from "../images/audio-cover-3.png";
import cover4 from "../images/audio-cover-4.png";

const GENERATED_COVERS = [cover1, cover2, cover3, cover4];

interface UseSfxGenerationParams {
  showSignUpPopover: (trigger: string) => void;
}

export function useSfxGeneration({ showSignUpPopover }: UseSfxGenerationParams) {
  const blobUrlsRef = useRef<string[]>([]);
  const generationRoundRef = useRef(0);

  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const generateItems = useCallback(
    async (prompt: string, loop: boolean): Promise<GeneratedSoundItem[]> => {
      try {
        const results = await generateTtsfx({ source: "programmatic", config: { text: prompt, loop } });

        const round = ++generationRoundRef.current;
        return results.map((r, i) => {
          const id = r.sound_generation_history_item?.sound_generation_history_item_id ?? `generated-${round}-${i}`;

          let audioUrl: string;
          if (r.audio_url) {
            audioUrl = r.audio_url;
          } else {
            const byteChars = atob(r.waveform_base_64);
            const byteArray = new Uint8Array(byteChars.length);
            for (let j = 0; j < byteChars.length; j++) byteArray[j] = byteChars.charCodeAt(j);
            const blob = new Blob([byteArray], { type: "audio/ogg; codecs=opus" });
            audioUrl = URL.createObjectURL(blob);
            blobUrlsRef.current.push(audioUrl);
          }

          const title = `Generation ${i + 1}`;

          return {
            id,
            title,
            audioUrl,
            isLoop: loop,
            coverImage: GENERATED_COVERS[i % GENERATED_COVERS.length].src,
          };
        });
      } catch (err) {
        if (typeof err === "object" && err !== null && "status" in err) {
          const errorStatus = (err as { status: string }).status;
          if (errorStatus === "quota_exceeded") {
            showSignUpPopover("generation_quota_exceeded");
          }
        }
        throw err;
      }
    },
    [showSignUpPopover],
  );

  const handleGenerateNew = useCallback(() => {
    blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  }, []);

  return { generateItems, handleGenerateNew };
}
